import { computed } from "@preact/signals";
import { ChevronLeft, ChevronRight } from "lucide-preact";
import { useEffect, useRef } from "preact/hooks";
import { ix, selected, view } from "../state.ts";
import { currentPage, reveal } from "../viewer.ts";

// One stop per moment, including shared cues; match the editor's department visibility.
const hiddenDepartments = computed(() => view.value.hiddenDepartments);
const visibleMoments = computed(() => {
  const index = ix.value;
  const hidden = new Set(hiddenDepartments.value);
  return index?.orderedMoments().filter((moment) => index.cuesOf(moment.id).some((cue) => !hidden.has(cue.department) || !index.departments.has(cue.department))) ?? [];
});

export function CueNavigation() {
  const root = useRef<HTMLElement>(null);
  useEffect(() => {
    const element = root.current!;
    let frame = 0;
    let height = -1;
    const update = () => {
      frame = 0;
      const next = element.getBoundingClientRect().height;
      if (height === next) return;
      height = next;
      document.documentElement.style.setProperty("--cue-navigation-height", `${height}px`);
    };
    // Inset writes can resize the PDF/gutter. Keep them outside ResizeObserver delivery.
    const observer = new ResizeObserver(() => {
      if (!frame) frame = requestAnimationFrame(update);
    });
    observer.observe(element);
    update();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      document.documentElement.style.removeProperty("--cue-navigation-height");
    };
  }, []);
  const index = ix.value;
  const ref = selected.value;
  const cursor = ref?.kind === "moment" ? index?.moments.get(ref.id)
    : ref?.kind === "note" ? index?.doc.notes.find((note) => note.id === ref.id)
    : ref?.kind === "standby" ? index?.doc.standbys.find((standby) => standby.id === ref.id) : undefined;
  const moments = visibleMoments.value;
  const page = currentPage.value;
  const previous = moments.findLast((moment) => cursor ? index!.compareAnchored(moment, cursor) < 0 : moment.anchor.page <= page);
  const next = moments.find((moment) => cursor ? index!.compareAnchored(moment, cursor) > 0 : moment.anchor.page >= page);
  return (
    <nav class="cue-navigation" aria-label="Cue navigation" ref={root}>
      {([
        ["Previous cue", previous, ChevronLeft],
        ["Next cue", next, ChevronRight],
      ] as const).map(([label, moment, Icon]) => (
        <button
          type="button"
          aria-label={label}
          data-tooltip={label}
          data-tooltip-description={moment
            ? `Select and show ${index!.cuesOf(moment.id).filter((cue) => !view.value.hiddenDepartments.includes(cue.department) || !index!.departments.has(cue.department)).map((cue) => index!.cueRef(cue)).join(" · ")} on p. ${index!.pageLabel(moment.anchor.page)}. Does not open an editor or change zoom.`
            : moments.length ? `No ${label === "Previous cue" ? "earlier" : "later"} visible cue. Navigation does not wrap.` : "No cues match the visible departments."}
          disabled={!moment}
          onClick={() => moment && reveal({ kind: "moment", id: moment.id }, moment.anchor.page)}
        >
          <Icon aria-hidden="true" />
          <span>{label === "Previous cue" ? "Previous" : "Next"}</span>
        </button>
      ))}
    </nav>
  );
}
